import { useMemo, useState } from 'react';
import {
  useActiveSpeakerId,
  useLocalSessionId,
  useParticipantIds,
  useScreenShare,
} from '@daily-co/daily-react';

import type { CallLayout, ManualLayoutChoice } from '../types';

export interface CallLayoutState {
  activeLayout: CallLayout;
  manualLayoutChoice: ManualLayoutChoice;
  setManualLayoutChoice: (choice: ManualLayoutChoice) => void;
  showLayoutToggle: boolean;
  /** For speaker view main stage (null in grid/spotlight) */
  mainStageSessionId: string | null;
  /** For speaker sidebar or grid tiles */
  sidebarParticipantIds: string[];
  /** Whether a remote screen share is active */
  hasRemoteScreenShare: boolean;
  /** Session ID of the remote screen sharer (for rendering screenVideo) */
  screenShareSessionId: string | null;
  activeSpeakerId: string | null;
  localSessionId: string;
}

export function useCallLayout(hideSelfView = false): CallLayoutState {
  const allParticipantIds = useParticipantIds({ sort: 'joined_at' });
  const rawActiveSpeakerId = useActiveSpeakerId({ ignoreLocal: true });
  const { screens } = useScreenShare();
  const localSessionId = useLocalSessionId() ?? '';
  const [manualLayoutChoice, setManualLayoutChoice] = useState<ManualLayoutChoice>('speaker');

  const remoteParticipantIds = useMemo(
    () => allParticipantIds.filter((id) => id !== localSessionId),
    [allParticipantIds, localSessionId],
  );

  const hasRemoteScreenShare = useMemo(
    () => screens.some((s) => !s.local),
    [screens],
  );

  const screenShareSessionId = useMemo(() => {
    const remoteScreen = screens.find((s) => !s.local);
    return remoteScreen?.session_id ?? null;
  }, [screens]);

  // Fallback: when no one is speaking, use first remote participant
  const activeSpeakerId = rawActiveSpeakerId ?? remoteParticipantIds[0] ?? null;

  const totalParticipants = allParticipantIds.length;

  const activeLayout: CallLayout = useMemo(() => {
    // Rule 1: Remote screen share → force speaker view
    if (hasRemoteScreenShare) return 'speaker';
    // Rule 2: ≤2 participants → spotlight
    if (totalParticipants <= 2) return 'spotlight';
    // Rule 3: 3+ participants → user choice
    return manualLayoutChoice;
  }, [hasRemoteScreenShare, totalParticipants, manualLayoutChoice]);

  const showLayoutToggle = !hasRemoteScreenShare && totalParticipants >= 3;

  const { mainStageSessionId, sidebarParticipantIds } = useMemo(() => {
    const visibleIds = hideSelfView
      ? allParticipantIds.filter((id) => id !== localSessionId)
      : allParticipantIds;

    if (activeLayout === 'grid') {
      return {
        mainStageSessionId: null,
        sidebarParticipantIds: visibleIds,
      };
    }

    if (activeLayout === 'speaker') {
      if (hasRemoteScreenShare && screenShareSessionId) {
        // Screen share: main stage = shared screen, sidebar = all camera participants
        return {
          mainStageSessionId: screenShareSessionId,
          sidebarParticipantIds: visibleIds,
        };
      }

      // No screen share: main stage = active speaker, sidebar = everyone else
      return {
        mainStageSessionId: activeSpeakerId,
        sidebarParticipantIds: visibleIds.filter((id) => id !== activeSpeakerId),
      };
    }

    // Spotlight: no main stage or sidebar concept used
    return {
      mainStageSessionId: null,
      sidebarParticipantIds: [],
    };
  }, [
    activeLayout,
    hasRemoteScreenShare,
    screenShareSessionId,
    activeSpeakerId,
    allParticipantIds,
    hideSelfView,
    localSessionId,
  ]);

  return {
    activeLayout,
    manualLayoutChoice,
    setManualLayoutChoice,
    showLayoutToggle,
    mainStageSessionId,
    sidebarParticipantIds,
    hasRemoteScreenShare,
    screenShareSessionId,
    activeSpeakerId,
    localSessionId,
  };
}
